<template>
  <div>

    <Head title="PDF Files Management System" />
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center mb-8">
        <h1 class="text-3xl font-bold">PDF Files Management</h1>
        <span class="text-xs mx-2 rounded-full p-2 bg-gray-200">{{ channelsArray?.total || 0 }}</span>
      </div>
    </div>
    <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
      <div class="flex items-center w-full md:w-auto max-w-md">
        <div class="flex w-full bg-white shadow rounded">
          <input type="text" v-model="localFilters.search" @input="debounceFilter"
            class="w-full px-6 py-3 border-none rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="Search by part number or description..." />
          <button v-if="localFilters.search" @click="clearSearch" class="p-3 text-gray-500 hover:text-gray-700">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Filter Buttons -->
      <div class="flex flex-wrap gap-2">
        <button @click="filterByRecentlyAdded"
          class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Recently Added
        </button>
        <button @click="sortBy('part_number')"
          class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
            :class="{ 'transform rotate-180': localFilters.sortField === 'part_number' && localFilters.sortDir === 'DESC' }"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
          Part Number
        </button>
        <button @click="resetFilters"
          class="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm flex items-center gap-1"
          :class="{ 'hidden': !isFilterActive }">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset
        </button>
      </div>

      <div class="ml-auto">
        <button class="btn-blue" @click="showModal = true">
          <span>Add</span><span class="hidden md:inline">&nbsp;New Document</span>
        </button>
      </div>
    </div>
    <div class="bg-white shadow overflow-x-auto rounded-lg">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold bg-gray-50">
            <th class="pb-4 pt-6 px-6 cursor-pointer" @click="sortBy('part_number')">
              <div class="flex items-center">
                Part Number
                <svg v-if="localFilters.sortField === 'part_number'" class="h-4 w-4 ml-1"
                  :class="{ 'transform rotate-180': localFilters.sortDir === 'DESC' }" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th class="pb-4 pt-6 px-6 cursor-pointer" @click="sortBy('description')">
              <div class="flex items-center">
                Description
                <svg v-if="localFilters.sortField === 'description'" class="h-4 w-4 ml-1"
                  :class="{ 'transform rotate-180': localFilters.sortDir === 'DESC' }" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </div>
            </th>
            <th class="pb-4 pt-6 px-6">PDF File</th>
            <th class="pb-4 pt-6 px-6">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ch in (channelsArray?.data || [])" :key="ch.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t">
              <div class="flex items-center px-6 py-4"> {{ ch.part_number }}</div>
            </td>
            <td class="border-t">
              <div class="flex items-center px-6 py-4"> {{ ch.description }}</div>
            </td>
            <td class="border-t">
              <div class="flex items-center px-6 py-4">
                <a :href="pdfLink(ch.file_path)" target="_blank" class="flex items-center gap-1 hover:text-blue-800">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span class="truncate max-w-xs">{{ getFileName(ch.file_path) }}</span>
                </a>
              </div>
            </td>
            <td class="border-t">
              <div class="flex items-center px-6 py-4 gap-2">
                <button class="text-blue-600 hover:text-blue-800" @click="editDocument(ch)">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button class="text-red-600 hover:text-red-800" @click="deleteDocument(ch.id)">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="(channelsArray?.data?.length || 0) === 0">
            <td colspan="4" class="px-4 py-3 text-center text-sm text-gray-500">
              No results found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination v-if="channelsArray?.links?.length > 0" class="mt-6" :links="channelsArray.links"
      :total="channelsArray.total" />

    <!-- Add New Document Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-lg" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold">Add New Document</h2>
          <button @click="closeAddModal" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form @submit.prevent="submitNewChannel" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Part Number</label>
            <div class="relative">
              <input type="text" v-model="partNumberSearch" @input="searchPartNumbers" @focus="onPartNumberFocus"
                @blur="onPartNumberBlur" class="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Search for part number..." autocomplete="off" />
              <div v-if="filteredPartNumbers.length > 0 && showPartNumberDropdown"
                class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
                @mousedown.prevent>
                <div v-for="part in filteredPartNumbers" :key="part.id" @mousedown="selectPartNumber(part)"
                  class="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                  <div class="font-medium">{{ part.part_number }}</div>
                  <div class="text-sm text-gray-600 truncate">{{ part.description }}</div>
                </div>
              </div>
              <div v-if="filteredPartNumbers.length === 0 && partNumberSearch && showPartNumberDropdown"
                class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg p-4 text-center text-gray-500"
                @mousedown.prevent>
                No matching part numbers found
              </div>
            </div>
          </div>
          <div v-if="selectedPartNumber">
            <label class="block text-sm font-medium text-gray-700">Description</label>
            <p class="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">{{ selectedPartNumber.description }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">PDF File</label>
            <input type="file" @change="handleFileUpload" accept=".pdf"
              class="mt-1 block w-full border border-gray-300 rounded px-3 py-2" required />
            <p class="mt-1 text-xs text-gray-500">Maximum file size: 10MB</p>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button type="button" @click="closeAddModal"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
              Cancel
            </button>
            <button type="submit" :disabled="isSubmitting || !selectedPartNumber || !newChannel.file"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              :class="{ 'opacity-50 cursor-not-allowed': isSubmitting || !selectedPartNumber || !newChannel.file }">
              {{ isSubmitting ? 'Saving...' : 'Save Document' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Document Modal -->
    <div v-if="editModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-lg" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold">Edit Document</h2>
          <button @click="closeEditModal" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form @submit.prevent="submitEditChannel" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Part Number</label>
            <p class="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">{{ currentChannel.part_number }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Description</label>
            <p class="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">{{ currentChannel.description }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Current File</label>
            <p v-if="currentChannel.file_path"
              class="mt-1 p-2 bg-gray-50 border border-gray-200 rounded flex items-center">
              <a :href="pdfLink(currentChannel.file_path)" target="_blank"
                class="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span class="truncate max-w-xs">{{ getFileName(currentChannel.file_path) }}</span>
              </a>
            </p>
            <p v-else class="mt-1 p-2 bg-gray-50 border border-gray-200 rounded text-gray-500">No file attached</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">New PDF File (Leave empty to keep current)</label>
            <input type="file" @change="handleEditFileUpload" accept=".pdf"
              class="mt-1 block w-full border border-gray-300 rounded px-3 py-2" />
            <p class="mt-1 text-xs text-gray-500">Maximum file size: 10MB</p>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button type="button" @click="closeEditModal"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
              Cancel
            </button>
            <button type="submit" :disabled="isEditing || (!currentChannel.file && !currentChannel.file_path)"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              :class="{ 'opacity-50 cursor-not-allowed': isEditing || (!currentChannel.file && !currentChannel.file_path) }">
              {{ isEditing ? 'Updating...' : 'Update Document' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout.vue'
import debounce from 'lodash/debounce'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'ChannelsIndex',
  layout: Layout,
  components: { Head, Pagination },

  props: {
    channelsArray: {
      type: Object,
      required: true,
      default: () => ({
        data: [],
        links: [],
        total: 0
      }),
    },
    filters: {
      type: Object,
      required: true,
      default: () => ({
        search: '',
        sortField: 'id',
        sortDir: 'ASC',
      }),
    },
    partNumbers: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  data() {
    return {
      form: {
        search: this.filters?.search || '',
      },
      localFilters: {
        search: this.filters?.search || '',
        sortField: this.filters?.sortField || 'part_number',
        sortDir: this.filters?.sortDir || 'ASC',
        recentlyAdded: false,
      },
      showModal: false,
      editModal: false,
      newChannel: {
        file: null,
      },
      currentChannel: {
        id: null,
        part_number: '',
        description: '',
        file_path: '',
        file: null,
      },
      isSubmitting: false,
      isEditing: false,
      partNumberSearch: '',
      filteredPartNumbers: [],
      selectedPartNumber: null,
      showPartNumberDropdown: false,
      dropdownCloseTimeout: null,
    }
  },

  computed: {
    isFilterActive() {
      return this.localFilters.search ||
        this.localFilters.sortField !== 'part_number' ||
        this.localFilters.sortDir !== 'ASC' ||
        this.localFilters.recentlyAdded;
    },

    isRecentlyAddedActive() {
      return this.localFilters.recentlyAdded;
    }
  },

  methods: {
    pdfLink(filePath) {
      if (!filePath) return '';

      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
      }

      if (filePath.startsWith('/pdf-file/')) {
        return filePath;
      }

      const cleanPath = filePath.startsWith('specsheets/')
        ? filePath.substring('specsheets/'.length)
        : filePath;

      return `/pdf-file/${encodeURIComponent(cleanPath)}`;
    },

    getFileName(filePath) {
      if (!filePath) return '';

      const pathWithoutParams = filePath.split('?')[0];

      const cleanPath = pathWithoutParams.replace('/pdf-file/', '');

      const withoutPrefix = cleanPath.replace('specsheets/', '');

      return decodeURIComponent(withoutPrefix);
    },

    searchPartNumbers() {
      if (!this.partNumberSearch) {
        this.filteredPartNumbers = this.partNumbers.slice(0, 10);
        return;
      }

      const search = this.partNumberSearch.toLowerCase();
      this.filteredPartNumbers = this.partNumbers
        .filter(part =>
          part.part_number.toLowerCase().includes(search) ||
          part.description.toLowerCase().includes(search)
        )
        .slice(0, 10);
    },

    selectPartNumber(part) {
      this.selectedPartNumber = part;
      this.partNumberSearch = part.part_number;
      this.showPartNumberDropdown = false;

      // Clear any existing timeout
      if (this.dropdownCloseTimeout) {
        clearTimeout(this.dropdownCloseTimeout);
        this.dropdownCloseTimeout = null;
      }
    },

    onPartNumberFocus() {
      // Clear any existing timeout
      if (this.dropdownCloseTimeout) {
        clearTimeout(this.dropdownCloseTimeout);
        this.dropdownCloseTimeout = null;
      }

      this.showPartNumberDropdown = true;
      // Initialize dropdown with data if search field is empty
      if (!this.partNumberSearch) {
        this.filteredPartNumbers = this.partNumbers.slice(0, 10);
      }
    },

    onPartNumberBlur() {
      // Use timeout to allow click on dropdown item to register before closing
      this.dropdownCloseTimeout = setTimeout(() => {
        this.showPartNumberDropdown = false;
      }, 200);
    },

    sortBy(field) {
      if (this.localFilters.sortField === field) {
        // Toggle direction if already sorting by this field
        this.localFilters.sortDir = this.localFilters.sortDir === 'ASC' ? 'DESC' : 'ASC';
      } else {
        // Set new sort field and default to ascending
        this.localFilters.sortField = field;
        this.localFilters.sortDir = 'ASC';
      }

      // Reset recently added filter if present
      this.localFilters.recentlyAdded = false;

      this.applyFilters();
    },

    filterByRecentlyAdded() {
      // Toggle the recently added filter
      this.localFilters.recentlyAdded = !this.localFilters.recentlyAdded;

      if (this.localFilters.recentlyAdded) {
        // Store previous sort for when we reset
        this.previousSort = {
          field: this.localFilters.sortField,
          dir: this.localFilters.sortDir
        };

        // Sort by id descending for most recent first
        this.localFilters.sortField = 'id';
        this.localFilters.sortDir = 'DESC';
      } else {
        // If turning off, reset to previous sort or default
        if (this.previousSort) {
          this.localFilters.sortField = this.previousSort.field;
          this.localFilters.sortDir = this.previousSort.dir;
        } else {
          this.localFilters.sortField = 'part_number';
          this.localFilters.sortDir = 'ASC';
        }
      }

      this.applyFilters();
    },

    resetFilters() {
      this.localFilters = {
        search: '',
        sortField: 'part_number',
        sortDir: 'ASC',
        recentlyAdded: false
      };

      this.applyFilters();
    },

    debounceFilter() {
      if (this.debouncedFilter) {
        this.debouncedFilter();
      }
    },

    clearSearch() {
      this.localFilters.search = '';
      this.applyFilters();
    },

    applyFilters() {
      const params = {
        search: this.localFilters.search,
        sortField: this.localFilters.sortField,
        sortDir: this.localFilters.sortDir,
      };

      router.get(
        '/pdf-channels',
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['channelsArray'],
        }
      )
    },

    handleFileUpload(event) {
      if (event.target.files && event.target.files.length > 0) {
        this.newChannel.file = event.target.files[0]
      }
    },

    async submitNewChannel() {
      if (!this.selectedPartNumber || !this.newChannel.file) return;

      this.isSubmitting = true
      try {
        const formData = new FormData()
        formData.append('part_number_id', this.selectedPartNumber.id)
        formData.append('file', this.newChannel.file)

        await router.post(route('pdf.channels.store'), formData, {
          onSuccess: () => {
            this.closeAddModal();
            this.$emit('success', 'Document successfully added');
          },
          onError: (errors) => {
            console.error(errors);
            this.$emit('error', 'Error adding document');
          },
        })
      } finally {
        this.isSubmitting = false
      }
    },

    closeAddModal() {
      this.showModal = false;
      this.newChannel = { file: null };
      this.selectedPartNumber = null;
      this.partNumberSearch = '';
      this.showPartNumberDropdown = false;

      // Clear any existing timeout
      if (this.dropdownCloseTimeout) {
        clearTimeout(this.dropdownCloseTimeout);
        this.dropdownCloseTimeout = null;
      }
    },

    editDocument(ch) {
      if (!ch) return;

      this.currentChannel = {
        id: ch.id,
        part_number: ch.part_number || '',
        description: ch.description || '',
        file_path: ch.file_path || '',
        file: null
      };
      this.editModal = true;
    },

    closeEditModal() {
      this.editModal = false;
      this.currentChannel = {
        id: null,
        part_number: '',
        description: '',
        file_path: '',
        file: null
      };
    },

    handleEditFileUpload(event) {
      if (event.target.files && event.target.files.length > 0) {
        this.currentChannel.file = event.target.files[0]
      }
    },

    async submitEditChannel() {
      if (!this.currentChannel.id) return;
      if (!this.currentChannel.file && !this.currentChannel.file_path) return;

      this.isEditing = true
      try {
        const formData = new FormData()

        if (this.currentChannel.file) {
          formData.append('file', this.currentChannel.file)
        }

        await router.post(route('pdf.channels.update', this.currentChannel.id), formData, {
          onSuccess: () => {
            this.closeEditModal();
            this.$emit('success', 'Document successfully updated');
          },
          onError: (errors) => {
            console.error(errors);
            this.$emit('error', 'Error updating document');
          },
          method: 'put',
        })
      } finally {
        this.isEditing = false
      }
    },

    async deleteDocument(id) {
      if (!id) return;

      if (confirm('Are you sure you want to delete this document?')) {
        try {
          await router.delete(route('pdf.channels.destroy', id), {
            onSuccess: () => {
              this.$emit('success', 'Document successfully deleted')
            },
            onError: (errors) => {
              console.error(errors);
              this.$emit('error', 'Error deleting document')
            },
          })
        } catch (error) {
          console.error('Error deleting document:', error)
        }
      }
    },

    hidePartNumberDropdown() {
      // Only hide if not in modal
      if (!this.showModal) {
        this.showPartNumberDropdown = false;
      }
    }
  },

  created() {
    this.debouncedFilter = debounce(() => {
      this.applyFilters();
    }, 300);

    // Initialize with first 10 part numbers
    this.filteredPartNumbers = this.partNumbers.slice(0, 10);

    // Only add global click handler to document for dropdown closing
    // but we'll handle it more carefully now
    document.addEventListener('click', (e) => {
      // Only process if dropdown is shown and click is outside of our dropdown area
      const dropdownEl = document.querySelector('.relative');
      if (this.showPartNumberDropdown && dropdownEl && !dropdownEl.contains(e.target)) {
        this.hidePartNumberDropdown();
      }
    });
  },

  beforeUnmount() {
    // Clean up any timeouts
    if (this.dropdownCloseTimeout) {
      clearTimeout(this.dropdownCloseTimeout);
    }

    // Remove event listener
    document.removeEventListener('click', this.hidePartNumberDropdown);
  },

  watch: {
    'localFilters.search': {
      handler() {
        this.debounceFilter();
      }
    }
  },
}
</script>

<style scoped>
.btn-blue {
  @apply px-4 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500;
}
</style>